import c from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Icon } from '@repo/ui';
import consults1on1Image from 'assets/images/consults_method_1on1.png';
import consultsGroupImage from 'assets/images/consults_method_group.png';
import {
  Button,
  ButtonColors,
  Card,
  ContainerSizes,
  PageContainer,
  Typography,
} from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useSubscriptions from 'services/general/subscriptionsList';
import { getConsultCreditsCount } from 'store/account';

import styles from './ConsultsSelectMethod.module.css';

const consultOptions = [
  {
    id: 'group',
    imgUrl: consultsGroupImage,
    label: 'Wait: < 1 week',
    headline: 'Personalized Results Review',
    subheadline:
      'Small groups of ~4 people where you can get individual attention from a specialist',
    tagLabel: 'Recommended',
  },
  {
    id: 'individual',
    imgUrl: consults1on1Image,
    label: 'Wait: 4+ weeks',
    headline: '1:1 call with a specialist',
    subheadline: '20 min appointment for complex topics and conditions',
    tagLabel: undefined,
  },
];

const REGULAR_PRICE = '$79';
const DISCOUNT_PRICE = '$39';

interface IndividualPriceProps {
  creditsCount: number;
  isTinyPlus: boolean;
}

const IndividualPrice: React.FC<IndividualPriceProps> = ({
  creditsCount,
  isTinyPlus,
}) => {
  const discountedPrice = getDiscountedPrice({ creditsCount, isTinyPlus });

  const bottomPill = isTinyPlus ? (
    <div className={styles.tinyPlusTag}>
      <Icon name='awardFilled' size='s' />
      <Typography variant='label'>Tiny+ discount applied.</Typography>
    </div>
  ) : (
    <div className={styles.tinyPlusOfferTag}>
      <Icon name='awardFilled' size='s' />
      <Typography variant='label'>Tiny+ members save $40 per call</Typography>
    </div>
  );

  if (discountedPrice !== null) {
    return (
      <div className={styles.priceArea}>
        <div className={styles.priceRow}>
          <Typography variant='heading-l' className={styles.paymentInfo}>
            {discountedPrice}
          </Typography>
          <Typography
            variant='heading-l'
            className={styles.paymentInfoStrikethrough}
          >
            {REGULAR_PRICE}
          </Typography>
        </div>
        {bottomPill}
      </div>
    );
  }

  return (
    <div className={styles.priceArea}>
      <Typography variant='heading-l' className={styles.paymentInfo}>
        {REGULAR_PRICE}
      </Typography>
      <div className={styles.tinyPlusOfferTag}>
        <Icon name='awardFilled' size='s' />
        <Typography variant='label'>Tiny+ members save $40 per call</Typography>
      </div>
    </div>
  );
};

const getDiscountedPrice = ({
  creditsCount,
  isTinyPlus,
}: IndividualPriceProps) => {
  const hasFreeSession = creditsCount > 0;
  if (hasFreeSession && isTinyPlus) {
    return creditsCount === 1
      ? '1 free session'
      : `${creditsCount} free sessions`;
  } else if (hasFreeSession) {
    return creditsCount === 1
      ? '1 free credit'
      : `${creditsCount} free credits`;
  }

  if (isTinyPlus) {
    return DISCOUNT_PRICE;
  }

  // Show no discount
  return null;
};

interface ConsultMethodCardProps {
  imgUrl: string;
  label: string;
  headline: string;
  subheadline: string;
  tagLabel?: string;
  isSelected: boolean;
  onClick: () => void;
  priceContent: React.ReactNode;
}

const ConsultMethodCard: React.FC<ConsultMethodCardProps> = ({
  imgUrl,
  //label,
  headline,
  subheadline,
  tagLabel,
  isSelected,
  onClick,
  priceContent,
}) => {
  // Temporarily commenting out label due to design changes (See #ENG-3663)
  return (
    <Card
      className={c(styles.card, { [styles.selected]: isSelected })}
      onClick={onClick}
      variant='wrapper'
    >
      <div className={styles.cardContainer}>
        <img src={imgUrl} alt={headline} className={styles.image} />
        <div className={styles.contentContainer}>
          {tagLabel && (
            <Typography variant='caption' className={styles.recommendedTag}>
              {tagLabel}
            </Typography>
          )}
          {/*<Typography variant='label' className={styles.label}>
            {label}
          </Typography>*/}
          <Typography variant='heading-m' className={styles.headline}>
            {headline}
          </Typography>
          <Typography variant='label' className={styles.subheadline}>
            {subheadline}
          </Typography>
          {priceContent}
        </div>
      </div>
    </Card>
  );
};

const ConsultsSelectMethod: React.FC = () => {
  const consultCreditsCount = useSelector(getConsultCreditsCount);
  const hasAvailableConsultCredit = (consultCreditsCount ?? 0) > 0;
  const { isAnyTinyPlus, isLoading: isSubscriptionsLoading } =
    useSubscriptions();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const navigate = useNavigate();
  const { kitId } = useParams<{ kitId: string }>();
  const { trackEvent, trackEventOnce } = useAnalytics();

  useEffect(() => {
    if (isSubscriptionsLoading) return;
    trackEventOnce('Consult Method Page Viewed', kitId ?? '', {
      is_tiny_plus_member: isAnyTinyPlus,
    });
  }, [isAnyTinyPlus, isSubscriptionsLoading, kitId, trackEventOnce]);

  const handleMethodSelect = (methodId: string) => {
    setSelectedMethod(methodId);
    if (methodId === 'group') {
      trackEvent('Personalized Video Review Selected');
    } else if (methodId === 'individual') {
      trackEvent('1:1 Call Selected');
    }
  };

  const handleNext = () => {
    if (selectedMethod) {
      navigate(`/results/${kitId}/consults/book?method=${selectedMethod}`);
    }
  };

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.container}>
      <Typography variant='heading-l' className={styles.title}>
        How would you like to review your results?
      </Typography>
      <Typography variant='label' className={styles.subtitle}>
        Choose from the below options to use it:
      </Typography>
      <div className={styles.cardsContainer}>
        {consultOptions.map(option => (
          <ConsultMethodCard
            key={option.id}
            imgUrl={option.imgUrl}
            label={option.label}
            headline={option.headline}
            subheadline={option.subheadline}
            tagLabel={hasAvailableConsultCredit ? undefined : option.tagLabel}
            isSelected={selectedMethod === option.id}
            onClick={() => handleMethodSelect(option.id)}
            priceContent={
              option.id === 'group' ? (
                <Typography variant='heading-l' className={styles.paymentInfo}>
                  Free
                </Typography>
              ) : (
                <IndividualPrice
                  creditsCount={consultCreditsCount ?? 0}
                  isTinyPlus={isAnyTinyPlus}
                />
              )
            }
          />
        ))}
      </div>
      <div className={styles.buttonContainer}>
        <Button
          onClick={handleNext}
          disabled={!selectedMethod}
          className={styles.nextButton}
          color={ButtonColors.PURPLE}
        >
          Next
        </Button>
      </div>
    </PageContainer>
  );
};

export default ConsultsSelectMethod;
